import React from 'react';
import { Link } from 'react-router-dom';

import { Callout, Step } from './shared';

const AiProviders: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">AI Providers &amp; Keys</h2>
    <p className="text-base-content/50 text-sm mb-5">Run MFlow&apos;s agents on your own model provider.</p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      By default, every agent runs on MFlow&apos;s hosted model. You can also add your own API key (&quot;bring your own
      key&quot;, or BYOK) for <strong>OpenAI</strong>, <strong>Claude (Anthropic)</strong>,{' '}
      <strong>Gemini (Google)</strong>, or <strong>DeepSeek</strong>. Your provider then bills you directly, and hosted
      usage limits don&apos;t apply.
    </p>

    <h3 className="font-semibold mb-3">Adding a key</h3>
    <div className="space-y-3 mb-5">
      <Step n={1}>
        Open <strong>Integrations</strong> and find the provider under the AI provider tiles.
      </Step>
      <Step n={2}>
        Paste your API key and save. MFlow checks the key with the provider when you save it. A key that passes shows a{' '}
        <strong>Verified</strong> badge. If the provider can&apos;t be reached, the key is still saved, just without the
        badge.
      </Step>
      <Step n={3}>
        After saving, only the last few characters of the key are shown (for example •••• a1b2). To replace a key, paste
        a new one into the same field. To remove it, click remove.
      </Step>
    </div>

    <Callout type="info">
      Keys are stored encrypted and are only ever read by the MFlow services that need them. Removing a key also clears
      it as your default provider.
    </Callout>

    <h3 className="font-semibold mb-2 mt-6">Which agents use your key</h3>
    <div className="overflow-x-auto mb-5">
      <table className="table table-sm text-sm">
        <thead>
          <tr>
            <th>Agent</th>
            <th>How the provider is chosen</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-medium whitespace-nowrap">Coding Agent</td>
            <td className="text-base-content/70">
              You pick it for each task when you start one. Any provider you&apos;ve saved a key for can be used, and
              BYOK tasks skip the hosted monthly task limit. See{' '}
              <Link to="/docs/coding-agent" className="link link-primary">
                Coding Agent
              </Link>
              .
            </td>
          </tr>
          <tr>
            <td className="font-medium whitespace-nowrap">Chat assistants</td>
            <td className="text-base-content/70">
              Use your <strong>default provider</strong>, set once in Settings (see below). This currently applies to
              the Standalone assistant.
            </td>
          </tr>
          <tr>
            <td className="font-medium whitespace-nowrap">Git Reviewer</td>
            <td className="text-base-content/70">
              Uses your default provider if it&apos;s OpenAI, Claude, or DeepSeek. Otherwise it uses MFlow&apos;s hosted
              Gemini.
            </td>
          </tr>
          <tr>
            <td className="font-medium whitespace-nowrap">Background jobs</td>
            <td className="text-base-content/70">
              Always hosted, never your key. This includes the weekly ADR drift check.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3 className="font-semibold mb-3">Choosing a default provider</h3>
    <div className="space-y-3 mb-5">
      <Step n={1}>
        Open <strong>Settings → AI agents → Bring your own key</strong>.
      </Step>
      <Step n={2}>
        Pick one of your saved keys from <strong>Default provider</strong>, or choose <em>mFlow-hosted (default)</em> to
        go back to MFlow&apos;s model. The list only shows providers you&apos;ve already saved a key for.
      </Step>
    </div>

    <Callout type="tip">
      The default provider doesn&apos;t change the Coding Agent. Each coding task still uses the provider you choose
      when you start it.
    </Callout>
  </div>
);

export default AiProviders;
